import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react'
import { cx } from './primitives'

/**
 * A table that scrolls inside its own container. Wide data is normal here - a
 * quotation list carries a number, a customer, a status and four figures - and a
 * page that scrolls sideways is a page nobody can read.
 */
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  )
}

export function Th({ className, numeric, children, ...rest }: ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <th
      scope="col"
      className={cx(
        // Padding via .qh-th so a sortable header can actually zero it. See index.css.
        'qh-th border-b border-line text-[11px] font-medium uppercase tracking-[0.08em] text-muted',
        numeric ? 'text-right' : 'text-left',
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  )
}

/**
 * A column header that sorts.
 *
 * <p>
 * A real button inside the th, so it is reachable by keyboard and announced as a control,
 * and aria-sort so a screen reader states the current order rather than leaving the arrow
 * as decoration.
 * </p>
 *
 * Three states, not two: ascending, descending, then back to the listing's own default.
 * Two-state cycling traps somebody who sorted by customer and wants the newest quotation
 * back at the top without reloading the page.
 *
 * A column only becomes one of these when the API declares that field sortable. A header
 * that looks clickable and does nothing is worse than one that never offered.
 */
export function SortableTh({
  field,
  sort,
  onSortChange,
  numeric,
  className,
  children,
}: {
  /** The wire key, e.g. `name`. Descending is the same key with a leading minus. */
  field: string
  /** The listing's whole sort state, so the header and the toolbar stay one thing. */
  sort: string
  onSortChange: (sort: string) => void
  numeric?: boolean
  className?: string
  children: ReactNode
}) {
  const ascending = sort === field
  const descending = sort === `-${field}`
  const active = ascending || descending

  return (
    <Th
      numeric={numeric}
      className={cx('p-0', className)}
      aria-sort={ascending ? 'ascending' : descending ? 'descending' : 'none'}
    >
      <button
        type="button"
        onClick={() => onSortChange(ascending ? `-${field}` : descending ? '' : field)}
        // The button carries the cell's padding so the whole header is the hit target.
        // px-5 py-2.5 matches .qh-th exactly, which is what keeps a sortable column's
        // text on the same line as an unsortable one's.
        className={cx(
          'group flex w-full items-center gap-1 px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.08em] transition-colors hover:text-ink focus-visible:text-ink',
          numeric ? 'justify-end' : 'justify-start',
          active ? 'text-ink' : 'text-muted',
        )}
      >
        {children}

        {/* Nothing at rest. Nine arrows across a header row is noise, and the one that
            matters stops standing out. */}
        <svg
          aria-hidden
          viewBox="0 0 12 12"
          className={cx(
            'size-2.5 shrink-0 transition-opacity',
            active
              ? 'text-accent opacity-100'
              : 'text-faint opacity-0 group-hover:opacity-60 group-focus-visible:opacity-60',
            descending && 'rotate-180',
          )}
        >
          <path d="M6 2.5 2.5 7h7z" fill="currentColor" />
        </svg>
      </button>
    </Th>
  )
}

export function Td({ className, numeric, children, ...rest }: TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <td
      className={cx(
        // px-5, not px-4: the card around a table pads its header, its toolbar and its
        // footer to 1.25rem, and a column inset by less than that reads as misaligned
        // with the heading above it and the total below it.
        'border-b border-line-soft px-5 py-3 align-middle',
        numeric && 'tabular text-right',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  )
}

export function Tr({
  onClick,
  children,
}: {
  onClick?: () => void
  children: ReactNode
}) {
  if (!onClick) return <tr>{children}</tr>

  return (
    <tr
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
        }
      }}
      tabIndex={0}
      role="button"
      className="cursor-pointer hover:bg-surface-2 focus-visible:bg-surface-2"
    >
      {children}
    </tr>
  )
}
